// main.js

// ==============================================================================
// Non-production tab favicon: production keeps the default icon set in
// index.html; development/test get a colored variant so tabs are easy to
// tell apart (same green/blue convention as the env banner in
// TopNavBarAltAltAlt.vue).
// ==============================================================================
(function setEnvironmentFavicon() {
  // Bump this suffix whenever the icon files' content changes -- browsers
  // cache favicons by URL and won't pick up new content otherwise.
  const FAVICON_VERSION = 'v2';
  const iconByMode = {
    development: `/static/logo/formue_favicon_dev.svg?${FAVICON_VERSION}`,
    test: `/static/logo/formue_favicon_test.svg?${FAVICON_VERSION}`,
  };
  const icon = iconByMode[import.meta.env.MODE];
  if (icon) {
    const link = document.querySelector('link[rel="icon"]');
    if (link) link.href = icon;
  }
})();

// ==============================================================================
// CRITICAL: Set up chunk load error handlers FIRST, before any imports
// ==============================================================================
console.log('[Init] Setting up chunk load error handlers');

const RELOAD_KEY = 'app-reload-attempt';
const RELOAD_MAX_ATTEMPTS = 3;
const APP_VERSION_KEY = 'app-version';

// Get current version from script tag
function getCurrentVersion() {
  const scripts = document.getElementsByTagName('script');
  for (let script of scripts) {
    const src = script.src;
    // Extract hash from filename like "index10313.js" or "main-abc123.js"
    const match = src.match(/\/([a-zA-Z]+)(\d+)\.js/);
    if (match) {
      return match[2]; // Return the hash/number
    }
  }
  return null;
}

// Check if we're running an old version
function checkVersion() {
  const currentVersion = getCurrentVersion();
  const storedVersion = localStorage.getItem(APP_VERSION_KEY);
  
  console.log('[Version Check]', { currentVersion, storedVersion });
  
  if (currentVersion) {
    if (storedVersion && storedVersion !== currentVersion) {
      console.log('[Version Check] Version mismatch detected! Forcing reload...');
      localStorage.removeItem(APP_VERSION_KEY);
      sessionStorage.clear();
      // Force a hard reload with cache bypass
      window.location.reload(true);
      return false;
    }
    localStorage.setItem(APP_VERSION_KEY, currentVersion);
  }
  return true;
}

// Run version check immediately
if (!checkVersion()) {
  // Halt execution if version mismatch detected
  throw new Error('Version mismatch - reloading');
}

// Intercept fetch to catch 404s on JavaScript files
const originalFetch = window.fetch;
window.fetch = function(...args) {
  return originalFetch.apply(this, args).then(response => {
    const url = args[0];
    // Check if this is a JavaScript file that returned 404
    if (typeof url === 'string' && url.includes('.js') && response.status === 404) {
      console.error('[Fetch Interceptor] JavaScript file not found:', url);
      console.log('[Fetch Interceptor] Forcing page reload to get latest version');
      localStorage.removeItem(APP_VERSION_KEY);
      sessionStorage.clear();
      setTimeout(() => window.location.reload(true), 100);
    }
    return response;
  });
};

// Early error detection
function isChunkLoadError(error) {
  if (!error) return false;
  const message = error.message || '';
  return (
    message.includes('Failed to fetch dynamically imported module') ||
    message.includes('Importing a module script failed') ||
    message.includes('Failed to load module script') ||
    message.includes('MIME type') ||
    message.includes('error loading dynamically imported module') ||
    error.name === 'ChunkLoadError' ||
    /dynamically imported module/.test(message) ||
    /loading chunk \d+ failed/i.test(message)
  );
}

function reloadWithSafetyEarly() {
  const attempts = parseInt(sessionStorage.getItem(RELOAD_KEY) || '0');
  
  if (attempts < RELOAD_MAX_ATTEMPTS) {
    sessionStorage.setItem(RELOAD_KEY, (attempts + 1).toString());
    console.log(`[Chunk Load] Reloading application (attempt ${attempts + 1}/${RELOAD_MAX_ATTEMPTS})`);
    setTimeout(() => {
      window.location.reload();
    }, 100);
  } else {
    console.error('[Chunk Load] Max reload attempts reached. Clearing cache required.');
    sessionStorage.removeItem(RELOAD_KEY);
    alert('Unable to load the latest version. Please clear your browser cache (Ctrl+Shift+Delete or Cmd+Shift+Delete) and reload manually.');
  }
}

// Set up global handlers IMMEDIATELY
window.addEventListener('error', (event) => {
  // Log ALL errors to debug
  console.log('[Error Handler] window.error caught:', {
    message: event.message,
    filename: event.filename,
    lineno: event.lineno,
    type: event.type,
    error: event.error
  });
  
  const message = event.message || '';
  const filename = event.filename || '';
  
  // Check for module script errors - be very aggressive
  const isModuleError = 
    message.includes('Failed to load module script') || 
    message.includes('MIME type') ||
    message.includes('dynamically imported module') ||
    (event.error && isChunkLoadError(event.error));
 
  if (isModuleError) {
    console.log('[Chunk Load] Module script error detected, forcing HARD reload');
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();
    
    // Clear all caches
    localStorage.removeItem(APP_VERSION_KEY);
    sessionStorage.clear();
    
    // Force hard reload immediately
    window.location.reload(true);
    return false;
  }
}, true);

window.addEventListener('unhandledrejection', (event) => {
  console.log('[Error Handler] unhandledrejection caught:', {
    reason: event.reason,
    promise: event.promise
  });
  
  if (event.reason && isChunkLoadError(event.reason)) {
    console.log('[Chunk Load] Async chunk load error detected, initiating reload');
    event.preventDefault();
    reloadWithSafetyEarly();
  }
});

console.log('[Init] Error handlers registered');

import { createApp } from 'vue';
import { createBootstrap } from 'bootstrap-vue-next'

import 'bootstrap/dist/css/bootstrap.css'
import 'bootstrap-vue-next/dist/bootstrap-vue-next.css'

import pinia from "stores";
import vSelect from 'vue-select';
import 'vue-select/dist/vue-select.css';
import Toast from "vue-toastification";
import "vue-toastification/dist/index.css";
import './assets/app.scss';
import './assets/custom.scss';
import { createRouter, createWebHistory } from 'vue-router';
import routes from './routes/routes';
import initProgress from './progressbar';
import axiosInstance from './store/axiosInstance';
import filters from './filters';
import 'bootstrap-icons/font/bootstrap-icons.css';
import App from './App.vue';

const app = createApp(App);

app.config.globalProperties.$filters = filters;

app.use(createBootstrap())
app.use(pinia);
app.component('v-select', vSelect);
app.use(Toast);

const router = createRouter({
  history: createWebHistory(),
  routes,
  linkActiveClass: 'active',
  scrollBehavior: () => false,
});
initProgress(router);

import { globalRouter } from './routes/globalRouter';
globalRouter.router = router;

app.use(router);

app.config.globalProperties.$axios = axiosInstance;

// Shortcut keys
import Vue3Shortkey from 'vue3-shortkey';
app.use(Vue3Shortkey);

app.config.globalProperties.$palette = function(n, names = null) {
  const colors = {
    maroon: [180, 0, 65],
    deep_maroon: [135, 0, 50],
    dark_maroon: [85, 0, 35],
    light_orange: [235, 130, 70],
    orange: [200, 90, 35],
    dark_orange: [135, 45, 5],
    light_green: [125, 170, 100],
    green: [75, 115, 65],
    dark_green: [30, 60, 30],
    light_blue: [150, 150, 245],
    blue: [75, 65, 190],
    dark_blue: [40, 30, 100],
    'Kontant': [150, 150, 245],
    'Obligationer': [75, 65, 190],
    'Aktier': [135, 45, 5],
    'Hedgefonde': [200, 90, 35],
    'Finansiering': [235, 130, 70],
    'Ejendomme': [125, 170, 100],
    'Private Equity': [75, 115, 65],
    'Infrastruktur': [30, 60, 30],
    'Multi Strategi': [180, 0, 65],
    'Direkte investeringer': [135, 0, 50],
    'Honorar': [80, 0, 35],
    'Buffer': [54, 201, 10],
  };
  if (names) {
    return names.map((nn) => `rgb(${colors[nn].join(',')})`);
  }
  if (n == 1) {
    names = ['dark_maroon'];
  } else if (n == 2) {
    names = ['dark_maroon', 'orange'];
  } else if (n == 3) {
    names = ['dark_maroon', 'orange', 'green'];
  } else if (n == 4) {
    names = ['dark_maroon', 'orange', 'green', 'blue'];
  }
  if (names) {
    return names.map((nn) => `rgb(${colors[nn].join(',')})`);
  }
  if (n <= Object.values(colors).length) {
    let cols = Object.values(colors).slice(0, n);
    return cols.map((nn) => `rgb(${nn.join(',')})`);
  }
  const formatNumber = function(x) {
    return Math.max(Math.min(Math.round(x), 255), 0);
  };
  const curveR = function(x) {
    let y = 6575 * x ** 4 - 14168 * x ** 3 + 9767 * x ** 2 - 2165.8 * x + 169.2;
    return formatNumber(y);
  };
  const curveG = function(x) {
    let y = 6239.1 * x ** 4 - 12994 * x ** 3 + 8338.2 * x ** 2 - 1722.6 * x + 151.22;
    return formatNumber(y);
  };
  const curveB = function(x) {
    let y = 415.22 * x ** 2 - 566.76 * x + 221.18;
    return formatNumber(y);
  };
  let increment = 1.0 / (n + 1);
  let res = [];
  var pos;
  for (var i = 0; i < n; i++) {
    pos = increment * (i + 1);
    res.push(`rgb(${curveR(pos)},${curveG(pos)},${curveB(pos)})`);
  }
  return res.reverse();
};

app.config.globalProperties.$resolveRoute = function(name) {
  if (this.$onInvestorRoute()) {
    return 'investor_' + name;
  } else {
    return name;
  }
};
app.config.globalProperties.$onInvestorRoute = function() {
  return this.$route.path.slice(0, 9) === '/investor';
};
app.config.globalProperties.$onEmployeeRoute = function() {
  return this.$route.path.slice(0, 9) === '/employee';
};

Date.prototype.to_s = function(){
  return this.getFullYear()+'-'+(this.getMonth()+1).pad(2)+'-'+this.getDate().pad(2)
}
Date.prototype.lastDateInMonth = function(){
  return new Date(this.getFullYear(), this.getMonth()+1, 0);
}
Date.prototype.to_da = function(){
  return this.getDate().pad(2)+'-'+(this.getMonth()+1).pad(2)+'-'+this.getFullYear()
}
Date.prototype.quarter_start = function(){
  const month = this.getMonth()
  const quarter_month = (Math.floor((month + 3) / 3) * 3) - 3
   return new Date(this.getFullYear(), quarter_month, 1);
}
Date.prototype.quarter_end = function(){
  const month = this.getMonth()
  const quarter_month = (Math.floor((month + 3) / 3) * 3)
  const n_date = new Date(this.getFullYear(), quarter_month, 1);
  return n_date.lastDateInMonth()
}
Date.prototype.getWeekNumber = function(){
  var d = new Date(Date.UTC(this.getFullYear(), this.getMonth(), this.getDate()));
  var dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  var yearStart = new Date(Date.UTC(d.getUTCFullYear(),0,1));
  return Math.ceil((((d - yearStart) / 86400000) + 1)/7)
}
Number.prototype.pad = function(size) {
  var s = String(this)
  while (s.length < (size || 2)) {s = "0" + s;}
  return s;
}
Number.prototype.round = function(digits){
  return Math.round(this*10**digits)/10**digits
}
Array.prototype.last = function(){
  return this[this.length - 1]
}
Array.prototype.first = function(){
  return this[0]
}
Array.prototype.sortBy = function(attr){
  return this.sort((a,b) => (a[attr] > b[attr]) ? 1 : -1)
}
Array.prototype.isEmpty = function(){
  return this.length === 0
}
String.prototype.reverse = function(){
  return this.split('').reverse().join('')
}
String.prototype.capitalize = function(){
  return this.charAt(0).toUpperCase() + this.slice(1)
}
String.prototype.camelize = function(){
  return this.split('_').map(s => s.capitalize()).join('')
}
Array.prototype.mean = function(){
  if(this.length > 0){
    return this.reduce((a,b)=>{return a+b}) / parseFloat(this.length)
  } else {
    return NaN
  }
}
Array.prototype.sample_variance = function(){
  if(this.length > 2){
    let m = this.mean()
    let sv = 0
    this.forEach(v => sv += (v-m)**2)
    return sv/(this.length - 1.0)
  } else {
    return NaN
  }
}
Array.prototype.standard_deviation = function(){
  if(this.length > 2){
    return this.sample_variance()**0.5
  } else {
    return NaN
  }
}

import { TinyEmitter } from 'tiny-emitter';
const emitter = new TinyEmitter();

emitter.on('scroll', (elementId) => {
  // Targets are usually emitted while the owning component is still mounting
  // (route driven selections, e.g. opening an account with CTRL-ALT-K), so wait
  // for the element instead of silently doing nothing when it is not there yet.
  let attempts = 0;
  const scrollWhenPresent = () => {
    const element = document.getElementById(elementId);
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "start" });
      return;
    }
    if (attempts++ < 20) {
      setTimeout(scrollWhenPresent, 100);
    }
  };
  scrollWhenPresent();
});

app.config.globalProperties.$emitter = emitter;

// ==============================================================================
// Additional chunk load error handlers (Vue-aware)
// ==============================================================================

// Enhanced reload with toast notification
function reloadWithToast() {
  const attempts = parseInt(sessionStorage.getItem(RELOAD_KEY) || '0');
  
  if (attempts < RELOAD_MAX_ATTEMPTS) {
    sessionStorage.setItem(RELOAD_KEY, (attempts + 1).toString());
    console.log(`[Chunk Load] Reloading with notification (attempt ${attempts + 1}/${RELOAD_MAX_ATTEMPTS})`);
    
    if (app.config.globalProperties.$toast) {
      app.config.globalProperties.$toast.info('New version detected. Updating application...', {
        timeout: 2000,
        closeOnClick: false,
        pauseOnHover: false,
      });
      setTimeout(() => window.location.reload(), 500);
    } else {
      window.location.reload();
    }
  } else {
    console.error('[Chunk Load] Max reload attempts reached');
    sessionStorage.removeItem(RELOAD_KEY);
    if (app.config.globalProperties.$toast) {
      app.config.globalProperties.$toast.error(
        'Unable to load the latest version. Please clear your browser cache and reload manually.',
        { timeout: false }
      );
    }
  }
}

// Vue error handler
app.config.errorHandler = (err, instance, info) => {
  if (isChunkLoadError(err)) {
    console.log('[Chunk Load] Vue caught chunk load error, reloading');
    reloadWithToast();
    return;
  }
  console.error('[App Error]', err, info);
  throw err;
};

// Router error handler
router.onError((error) => {
  if (isChunkLoadError(error)) {
    console.log('[Chunk Load] Router caught chunk load error, reloading');
    reloadWithToast();
  } else {
    console.error('[Router Error]', error);
  }
});

// Reset reload counter on successful navigation
router.isReady().then(() => {
  sessionStorage.removeItem(RELOAD_KEY);
});

app.mount('#app');

export default app;